import { useCallback, useState } from 'react'

import { captureException } from '@sentry/browser'
import {
  PayrollConceptFragment,
  PayrollResultLabelConceptMappingsSuggestion,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useResultLabelConceptMappingsSuggestions } from 'modules/payroll_bundle/hooks/useResultLabelConceptMappingsSuggestions'

type LabelToConceptId = { [key: string]: number | undefined }

const BATCH_SIZE = 12

type UseAutomapperReturn = {
  accumulatedSuggestions: PayrollResultLabelConceptMappingsSuggestion[]
  fetchingSuggestions: boolean
  autoMapLabels: (
    companyId: number,
    labelsToConcepts: LabelToConceptId,
    conceptList: PayrollConceptFragment[]
  ) => Promise<void>
}

export const useAutomapper = (initialStatus = false): UseAutomapperReturn => {
  const [accumulatedSuggestions, setAccumulatedSuggestions] = useState<
    PayrollResultLabelConceptMappingsSuggestion[]
  >([])
  const [fetchingSuggestions, setFetchingSuggestions] = useState<boolean>(initialStatus)

  const { getResultLabelConceptMappingsSuggestions } = useResultLabelConceptMappingsSuggestions()

  const showFlashMessage = useCallback(
    (type: 'wrong' | 'brand' | 'accent' | 'correct', message: string) => {
      app.setFlash({ type, message })
    },
    []
  )

  const getUnmappedLabelsAndConceptIds = useCallback(
    (labelsToConcepts: LabelToConceptId, conceptList: PayrollConceptFragment[]) => {
      const unmappedLabels = Object.keys(labelsToConcepts).filter(
        (label) => labelsToConcepts[label] === undefined
      )

      const mappedConceptIds = Object.values(labelsToConcepts).filter(
        (conceptId): conceptId is number => conceptId !== undefined
      )

      const mappedSet = new Set(mappedConceptIds)

      const unmappedConceptIds = conceptList
        .map(({ id }) => id)
        .filter((conceptId) => !mappedSet.has(conceptId))

      return { unmappedLabels, unmappedConceptIds }
    },
    []
  )

  const processBatch = (
    companyId: number,
    batch: string[],
    conceptIds: number[]
  ): Promise<PayrollResultLabelConceptMappingsSuggestion[]> => {
    return new Promise((resolve, reject) => {
      getResultLabelConceptMappingsSuggestions({
        variables: {
          companyId,
          labels: batch,
          conceptIds: conceptIds,
        },
        onError: (error) => {
          captureException(error)
          reject(error)
        },
        onCompleted: (data) => {
          resolve(data.payroll.resultLabelConceptMappingsSuggestionsConnection.nodes)
        },
      })
    })
  }

  const getNonSuggestedConcepts = (
    unmappedConceptIds: number[],
    suggestions: PayrollResultLabelConceptMappingsSuggestion[]
  ): number[] => {
    const matchedConceptIds = new Set(suggestions.map((s) => s.conceptId))
    return unmappedConceptIds.filter((id) => !matchedConceptIds.has(id))
  }

  const autoMapLabels = async (
    companyId: number,
    labelsToConcepts: LabelToConceptId,
    conceptList: PayrollConceptFragment[]
  ) => {
    let { unmappedLabels, unmappedConceptIds } = getUnmappedLabelsAndConceptIds(
      labelsToConcepts,
      conceptList
    )

    if (unmappedLabels.length === 0 || unmappedConceptIds.length === 0) {
      setFetchingSuggestions(false)
      return
    }

    setFetchingSuggestions(true)
    let allSuccess = true

    try {
      let suggestions: PayrollResultLabelConceptMappingsSuggestion[] = []

      for (let i = 0; i < unmappedLabels.length; i += BATCH_SIZE) {
        const batch = unmappedLabels.slice(i, i + BATCH_SIZE)
        unmappedConceptIds = getNonSuggestedConcepts(unmappedConceptIds, suggestions)

        const result = await processBatch(companyId, batch, unmappedConceptIds)
        if (result.length === 0) continue

        suggestions = [...suggestions, ...result]
      }

      const matchedLabels = new Set(suggestions.map((s) => s.label))
      unmappedLabels = unmappedLabels.filter((label) => !matchedLabels.has(label))
      unmappedConceptIds = getNonSuggestedConcepts(unmappedConceptIds, suggestions)

      if (unmappedLabels.length > 0) {
        const currentBatch = [...unmappedLabels]
        const result = await processBatch(companyId, currentBatch, unmappedConceptIds)

        if (result.length > 0) {
          suggestions = [...suggestions, ...result]
        }
      }

      setAccumulatedSuggestions((prev) => [...prev, ...suggestions])
    } catch (error) {
      allSuccess = false
    } finally {
      setFetchingSuggestions(false)
    }

    if (!allSuccess) {
      showFlashMessage(
        'accent',
        i18n.t(
          'payroll_bundle.results_payments.result_upload.map_to_concepts.auto_mapping.some_could_not_be_mapped'
        )
      )
      return
    } else {
      showFlashMessage(
        'correct',
        i18n.t(
          'payroll_bundle.results_payments.result_upload.map_to_concepts.auto_mapping.all_mapped'
        )
      )
    }
  }

  return { accumulatedSuggestions, fetchingSuggestions, autoMapLabels }
}
